import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../AuthModal';
import { Button } from '../ui/button';
import AddressForm from './AddressForm';

const AddressSection = ({
  addresses,
  selectedAddressId,
  setSelectedAddressId,
  showAddressForm,
  setShowAddressForm,
  newAddress,
  setNewAddress,
  addressErrors,
  handleSaveAddress,
  isSavingAddress,
  error,
  onAuthSuccess,
}) => {
  const { currentUser } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleAddNewAddressClick = () => {
    // Check if user is authenticated
    if (!currentUser) {
      // If not authenticated, show auth modal
      setShowAuthModal(true);
    } else {
      // If authenticated, show address form
      setShowAddressForm(true);
    }
  };

  const handleAuthSuccess = async () => {
    // Close the auth modal
    setShowAuthModal(false);

    // Call parent onAuthSuccess if provided to fetch addresses
    if (onAuthSuccess) {
      onAuthSuccess();
    }

    // After successful authentication, show the address form
    setShowAddressForm(true);
  };

  if (addresses.length === 0 && !showAddressForm) {
    return (
      <div className="bg-white border border-gray-200 p-4 rounded-md">
        <div className="text-center">
          <p className="mb-3">
            You don't have any saved addresses. Add a delivery address to
            continue.
          </p>
          <Button onClick={handleAddNewAddressClick} className="w-full">
            Add New Address
          </Button>

          {/* Auth Modal */}
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onAuthSuccess={handleAuthSuccess}
          />
        </div>
      </div>
    );
  }

  if (showAddressForm) {
    return (
      <div className="bg-white border border-gray-200 p-4 rounded-md">
        <AddressForm
          newAddress={newAddress}
          setNewAddress={setNewAddress}
          addressErrors={addressErrors}
          handleSaveAddress={handleSaveAddress}
          isSavingAddress={isSavingAddress}
          setShowAddressForm={setShowAddressForm}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {addresses.map((address) => (
        <div
          key={address.id}
          className={`border rounded-md p-3 cursor-pointer transition ${
            selectedAddressId === address.id
              ? 'border-primary bg-primary/5'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => setSelectedAddressId(address.id)}
        >
          <div className="flex items-start">
            <div className="w-5 h-5 mt-0.5 mr-3 rounded-full border border-gray-300 flex items-center justify-center">
              {selectedAddressId === address.id && (
                <div className="w-3 h-3 rounded-full bg-primary"></div>
              )}
            </div>
            <div className="flex-1">
              <p className="font-medium">
                {address.recipient}
                {address.is_default && (
                  <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                    Default
                  </span>
                )}
              </p>
              <p className="text-sm text-gray-600">
                {address.line1}
                {address.line2 && `, ${address.line2}`}
              </p>
              <p className="text-sm text-gray-600">
                {address.city}, {address.state} - {address.postal_code}
              </p>
              <p className="text-sm text-gray-600">{address.country}</p>
              <p className="text-sm text-gray-600 mt-1">
                Phone: {address.phone}
              </p>
            </div>
          </div>
        </div>
      ))}
      <div className="flex space-x-3 mt-3">
        <Button
          variant="outline"
          type="button"
          onClick={handleAddNewAddressClick}
          className="w-full"
        >
          Add New Address
        </Button>

        {/* Auth Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={handleAuthSuccess}
        />
      </div>
    </div>
  );
};

export default AddressSection;
